/**
 * adminMenus — single source of truth for admin sidebar (and any future menus).
 *
 * This file replaces:
 *   - The backend `menuStructure` returned in login response (gone).
 *   - The frontend `MENU_PERMISSION_MAP` scattered map (gone).
 *   - The `extractAllMenuIds`/`hasMenuItemById` indirection (gone).
 *
 * How it works:
 *   1. Each menu item has a `permission` (or `permissions: 'any-of'`) drawn
 *      from the type-safe `PermissionCode` union in `permissionCatalog`.
 *   2. The Sidebar consumes this list + the user's `permissions` set from
 *      `useAccess()` and filters in one pass.
 *   3. Backend's `authorize()` middleware is the SECURITY gate. This file
 *      governs only VISIBILITY. Hiding a menu does not protect anything by
 *      itself; the backend independently enforces every API call.
 *
 * Types are derived from the catalog so a typo here is a compile error.
 */

import type { PermissionCode } from './permissionCatalog';

/** Visibility predicate: at least one of the listed permissions is required. */
export type MenuPermissionRule = PermissionCode | PermissionCode[];

export interface MenuLeaf {
  /** Stable id, used as React key and for analytics */
  id: string;
  /** URL path (relative to /admin panel root) */
  path: string;
  /** Persian label */
  label: string;
  /** Material icon name (resolved by the Sidebar — keeps this file decoupled from MUI) */
  icon: string;
  /**
   * Permission(s) needed to see this leaf.
   *
   * `null` = visible to EVERY authenticated admin. Reserve it for self-scoped
   * pages whose backend endpoint takes no target user and can only ever return
   * the caller's own data (e.g. /my-tasks → GET work-assignment/my-tasks, which
   * hard-codes req.user.id). Gating those would only hide an admin's own data
   * from them. It must be spelled explicitly — a missing field is a type error,
   * so no leaf becomes public by accident.
   */
  permission: MenuPermissionRule | null;
  /** Optional badge (e.g. count) — populated at runtime by Sidebar */
  badgeKey?: string;
}

export interface MenuGroup {
  id: string;
  label: string;
  icon: string;
  /** Group is shown if user has at least one child's permission */
  children: MenuLeaf[];
  /** Optional explicit gate for the group itself (rare) */
  permission?: MenuPermissionRule;
}

export type MenuEntry = MenuLeaf | MenuGroup;

/**
 * High-level visual sections for the sidebar. Each entry in `ADMIN_MENUS` is
 * placed under a section by `getMenuSection(entryId)`. Section headers render
 * above the first entry of each section in the sidebar.
 */
export type SidebarSection =
  | 'overview'      // داشبورد، آنالیتیکس
  | 'operations'    // KYC، اختلافات، بنرها (صف‌های روزانه)
  | 'catalog'       // فروشندگان، محصولات، دسته‌بندی
  | 'commerce'      // سفارش‌ها، مالی
  | 'engagement'    // چت، تیکت، مقالات، نظرات
  | 'governance'    // بازرسی، قرعه‌کشی، اعلان‌ها
  | 'admin';        // کاربران، شیفت‌بندی، سیستم

export const SIDEBAR_SECTIONS: { id: SidebarSection; label: string }[] = [
  { id: 'overview', label: 'نمای کلی' },
  { id: 'operations', label: 'صف‌های عملیاتی' },
  { id: 'catalog', label: 'محتوا و کاتالوگ' },
  { id: 'commerce', label: 'سفارش و مالی' },
  { id: 'engagement', label: 'ارتباط با کاربر' },
  { id: 'governance', label: 'بازرسی و کنترل' },
  { id: 'admin', label: 'مدیریت سیستم' },
];

const ENTRY_TO_SECTION: Record<string, SidebarSection> = {
  dashboard: 'overview',
  'my-tasks': 'overview',
  'analytics-group': 'overview',
  'kyc-group': 'operations',
  'disputes-group': 'operations',
  'wholesale-group': 'operations',
  'banners-group': 'operations',
  'sellers-group': 'catalog',
  'products-group': 'catalog',
  'catalog-group': 'catalog',
  orders: 'commerce',
  'finance-group': 'commerce',
  chats: 'engagement',
  'chat-oversight': 'engagement',
  userbot: 'engagement',
  'telegram-channels': 'engagement',
  'tickets-group': 'engagement',
  'articles-group': 'engagement',
  reviews: 'engagement',
  'review-reports': 'engagement',
  stories: 'engagement',
  inspections: 'governance',
  'buyer-requests': 'governance',
  lottery: 'governance',
  raffle: 'governance',
  notifications: 'governance',
  'users-group': 'admin',
  'work-group': 'admin',
  'system-group': 'admin',
};

export function getMenuSection(entryId: string): SidebarSection {
  return ENTRY_TO_SECTION[entryId] ?? 'admin';
}

export const isMenuGroup = (e: MenuEntry): e is MenuGroup =>
  'children' in e && Array.isArray((e as MenuGroup).children);

// ---------------------------------------------------------------------------
// THE MENU TREE
// ---------------------------------------------------------------------------

export const ADMIN_MENUS: MenuEntry[] = [
  // ----- Top-level dashboards -----
  {
    id: 'dashboard',
    path: '/dashboard',
    label: 'داشبورد',
    icon: 'Dashboard',
    permission: 'admin_dashboard',
  },
  {
    id: 'my-tasks',
    path: '/my-tasks',
    label: 'کارهای من',
    icon: 'AssignmentInd',
    // Self-scoped — see MenuLeaf.permission. The endpoint returns only the
    // caller's own assigned items, so every admin may open this page.
    permission: null,
  },

  // ----- Operational queues (shift-gated) -----
  {
    id: 'kyc-group',
    label: 'احراز هویت (KYC)',
    icon: 'VerifiedUser',
    children: [
      { id: 'kyc-list', path: '/kyc', label: 'لیست KYC', icon: 'List', permission: 'admin_kyc_list' },
    ],
  },
  {
    id: 'disputes-group',
    label: 'اختلافات',
    icon: 'Gavel',
    children: [
      { id: 'dispute-center', path: '/dispute-center', label: 'مرکز داوری', icon: 'Gavel', permission: ['admin_unified_disputes_list', 'admin_unified_disputes_stats', 'admin_disputes_pending', 'admin_disputes_stats'] },
      { id: 'disputes', path: '/disputes', label: 'اختلافات سفارش', icon: 'Gavel', permission: 'admin_disputes_pending' },
    ],
  },
  // ⭐ Phase 7 — Wholesale Network
  {
    id: 'wholesale-group',
    label: 'عمده‌فروشی',
    icon: 'Warehouse',
    permission: 'admin_wholesale_view',
    children: [
      { id: 'wholesale', path: '/wholesale', label: 'مدیریت سیستم عمده‌فروشی', icon: 'Settings', permission: 'admin_wholesale_view' },
    ],
  },
  {
    id: 'banners-group',
    label: 'بنرها و پاپ‌آپ‌ها',
    icon: 'CampaignOutlined',
    children: [
      { id: 'banner-slots', path: '/admin/banners/slots', label: 'محل‌های بنر', icon: 'CampaignOutlined', permission: 'admin_banner_slots_read' },
      { id: 'banner-approvals', path: '/admin/banners/pending-approvals', label: 'تایید بنرها', icon: 'VerifiedUser', permission: 'admin_banner_approvals_read' },
      { id: 'banner-my', path: '/admin/banners/my-banners', label: 'بنرهای سایت', icon: 'CampaignOutlined', permission: ['admin_banner_create', 'admin_banner_my_banners_read'] },
      { id: 'site-popups', path: '/admin/popups', label: 'پاپ‌آپ‌های سایت', icon: 'Notifications', permission: 'admin_site_popup_read' },
      { id: 'blog-ads', path: '/admin/blog-ads', label: 'تبلیغات بلاگ', icon: 'CampaignOutlined', permission: 'admin_blog_ad_read' },
      { id: 'audience-messaging', path: '/admin/audience-messaging', label: 'پیام به دستهٔ کاربران', icon: 'Campaign', permission: 'admin_announcement_read' },
      { id: 'announcements', path: '/admin/announcements', label: 'اطلاعیه همگانی', icon: 'Campaign', permission: 'admin_announcement_read' },
      { id: 'push-settings', path: '/admin/push-settings', label: 'پوش‌نوتیفیکیشن', icon: 'NotificationsActive', permission: 'admin_dashboard_overview' },
    ],
  },

  // ----- Sellers / Stores -----
  {
    id: 'sellers-group',
    label: 'فروشندگان',
    icon: 'Storefront',
    children: [
      // Legacy kebab-case codes removed Session 4. Both pages gate on the
      // canonical `admin_seller_view` permission (the actual middleware code).
      { id: 'store-applications', path: '/store-applications', label: 'درخواست‌های فروشندگی', icon: 'Storefront', permission: 'admin_seller_view' },
      { id: 'seller-verifications', path: '/seller-verifications', label: 'احراز فروشندگان', icon: 'VerifiedUser', permission: 'admin_seller_view' },
      { id: 'stores', path: '/stores', label: 'مدیریت فروشگاه‌ها', icon: 'Storefront', permission: 'admin_store_read' },
      { id: 'store-edit-requests', path: '/store-edit-requests', label: 'ویرایش فروشگاه‌ها', icon: 'Edit', permission: 'admin_get_all_store_edit_requests' },
    ],
  },

  // ----- Products / Catalog -----
  {
    id: 'products-group',
    label: 'محصولات',
    icon: 'Inventory',
    children: [
      { id: 'products', path: '/products', label: 'مدیریت محصولات', icon: 'Inventory', permission: 'admin_product_read' },
      { id: 'product-approvals', path: '/product-approvals', label: 'تایید محصولات', icon: 'VerifiedUser', permission: 'admin_product_pending' },
      { id: 'product-edit-requests', path: '/product-edit-requests', label: 'ویرایش محصولات', icon: 'Edit', permission: 'admin_product_edit_request_read' },
      { id: 'suppliers', path: '/suppliers', label: 'تأمین‌کنندگان و خرید', icon: 'ReceiptLong', permission: ['admin_supplier_read', 'admin_purchase_invoice_read'] },
    ],
  },
  {
    id: 'catalog-group',
    label: 'دسته‌بندی و مشخصات',
    icon: 'Category',
    children: [
      { id: 'categories', path: '/categories', label: 'دسته‌بندی‌ها (مگامنو)', icon: 'Category', permission: 'admin_categories' },
      { id: 'account-types', path: '/account-types', label: 'انواع محصول', icon: 'Category', permission: 'admin_account_types' },
      { id: 'activity-areas', path: '/activity-areas', label: 'حوزه‌های فعالیت', icon: 'BusinessCenter', permission: 'admin_activity_areas' },
      { id: 'specifications', path: '/specifications', label: 'مشخصات', icon: 'List', permission: 'admin_specifications' },
      { id: 'companies', path: '/companies', label: 'شرکت‌ها', icon: 'BusinessCenter', permission: 'admin_company_read' },
    ],
  },

  // ----- Orders / Finance -----
  { id: 'orders', path: '/orders', label: 'سفارش‌ها', icon: 'LocalShipping', permission: 'admin_order_read' },
  {
    id: 'finance-group',
    label: 'مالی',
    icon: 'BarChart',
    children: [
      { id: 'finance', path: '/finance', label: 'داشبورد مالی', icon: 'BarChart', permission: 'admin_finance_overview' },
      { id: 'finance-transactions', path: '/finance/transactions', label: 'دفتر تراکنش‌ها', icon: 'List', permission: 'admin_finance_ledger' },
      { id: 'finance-statements', path: '/finance/statements', label: 'گردش حساب', icon: 'AccountBalanceWallet', permission: 'admin_finance_statement' },
      { id: 'finance-reports', path: '/finance/reports', label: 'گزارش‌های دوره‌ای', icon: 'Assessment', permission: 'admin_finance_reports' },
      { id: 'finance-accounting', path: '/finance/accounting', label: 'حسابداری', icon: 'AccountBalance', permission: 'admin_finance_accounting' },
      { id: 'payment-logs', path: '/payment-logs', label: 'لاگ پرداخت', icon: 'MonetizationOn', permission: 'admin_payment_logs_list' },
      { id: 'snapppay', path: '/snapppay', label: 'مرکز اسنپ‌پی', icon: 'CreditScore', permission: 'admin_snapppay_read' },
      { id: 'digipay', path: '/digipay', label: 'مرکز دیجی‌پی', icon: 'Payment', permission: 'admin_digipay_read' },
      { id: 'bank-accounts', path: '/bank-accounts', label: 'حساب‌های بانکی', icon: 'AccountBalance', permission: 'admin_bank_account_read' },
      { id: 'withdrawals', path: '/withdrawals', label: 'برداشت فروشندگان', icon: 'MonetizationOn', permission: 'admin_withdrawal_read' },
      { id: 'user-withdrawals', path: '/user-withdrawal-requests', label: 'برداشت کاربران', icon: 'AccountBalance', permission: 'admin_user_withdrawal_read' },
      { id: 'wallet-admin', path: '/wallet-admin', label: 'مدیریت کیف پول', icon: 'AccountBalanceWallet', permission: ['admin_wallet_manage', 'admin_wallet_statistics'] },
      { id: 'seller-balance-settings', path: '/seller-balance-settings', label: 'تنظیمات مالی', icon: 'Settings', permission: ['admin_settings_view', 'admin_seller_balance_manage'] },
    ],
  },

  // ----- Tickets -----
  {
    id: 'tickets-group',
    label: 'پشتیبانی',
    icon: 'Support',
    children: [
      { id: 'tickets-dashboard', path: '/tickets', label: 'داشبورد تیکت‌ها', icon: 'Dashboard', permission: 'admin_tickets_dashboard' },
      { id: 'tickets-list', path: '/tickets/list', label: 'لیست تیکت‌ها', icon: 'ConfirmationNumber', permission: 'admin_tickets_list' },
      { id: 'tickets-canned', path: '/tickets/canned-responses', label: 'پاسخ‌های آماده', icon: 'QuestionAnswer', permission: 'admin_canned_list' },
      { id: 'tickets-macros', path: '/tickets/macros', label: 'ماکروها', icon: 'List', permission: 'admin_macros_list' },
      { id: 'tickets-metrics', path: '/tickets/metrics/advanced', label: 'متریک‌های پیشرفته', icon: 'Assessment', permission: 'admin_ticket_metrics_advanced' },
      { id: 'tickets-agents', path: '/tickets/reports/agents', label: 'گزارش agents', icon: 'PersonOutline', permission: 'admin_ticket_reports_agents' },
    ],
  },

  // ----- Articles -----
  {
    id: 'articles-group',
    label: 'مقالات',
    icon: 'Article',
    children: [
      { id: 'articles', path: '/articles', label: 'مدیریت مقالات', icon: 'Article', permission: 'admin_article_read' },
      { id: 'articles-edit-requests', path: '/article-edit-requests', label: 'درخواست ویرایش', icon: 'Edit', permission: 'admin_article_edit_request_read' },
      { id: 'articles-comments', path: '/article-comments', label: 'مدیریت کامنت', icon: 'Comment', permission: 'admin_article_comment_moderate' },
      // Gated on the MANAGE perm (not _read): _read is granted to on-shift moderators
      // (their moderation page needs it for category chips/filters), but these are
      // content-management modules → show only to admins who can actually manage taxonomy.
      { id: 'articles-categories', path: '/article-categories', label: 'دسته‌های مقاله', icon: 'Category', permission: 'admin_article_category_create' },
      { id: 'articles-tags', path: '/article-tags', label: 'تگ‌های مقاله', icon: 'LocalOffer', permission: 'admin_article_tag_create' },
      { id: 'articles-series', path: '/article-series', label: 'سری‌های مقاله', icon: 'List', permission: 'admin_article_series_create' },
      { id: 'articles-reports', path: '/article-reports', label: 'گزارش مقالات', icon: 'Assessment', permission: 'admin_article_report_read' },
    ],
  },

  // ----- Analytics -----
  {
    id: 'analytics-group',
    label: 'آنالیتیکس',
    icon: 'BarChart',
    children: [
      { id: 'analytics-overview', path: '/analytics', label: 'نمای کلی', icon: 'Dashboard', permission: 'admin_analytics_overview' },
      { id: 'analytics-realtime', path: '/analytics/realtime', label: 'بلادرنگ', icon: 'Update', permission: 'admin_analytics_realtime' },
      { id: 'analytics-acquisition', path: '/analytics/acquisition', label: 'جذب کاربر', icon: 'PersonOutline', permission: 'admin_analytics_acquisition' },
      { id: 'analytics-audience', path: '/analytics/audience', label: 'مخاطب', icon: 'People', permission: 'admin_analytics_audience' },
      { id: 'analytics-platforms', path: '/analytics/platforms', label: 'اپ در برابر وب', icon: 'PhoneIphone', permission: 'admin_analytics_platforms' },
      { id: 'analytics-behavior', path: '/analytics/behavior', label: 'رفتار کاربر', icon: 'Psychology', permission: 'admin_analytics_behavior' },
      { id: 'analytics-funnels', path: '/analytics/funnels', label: 'قیف‌ها', icon: 'FilterAlt', permission: 'admin_analytics_funnels' },
      { id: 'analytics-retention', path: '/analytics/retention', label: 'نگه‌داشت', icon: 'Loop', permission: 'admin_analytics_retention' },
      { id: 'analytics-performance', path: '/analytics/performance', label: 'عملکرد', icon: 'Speed', permission: 'admin_analytics_performance' },
      { id: 'analytics-marketplace', path: '/analytics/marketplace', label: 'بازار', icon: 'Storefront', permission: 'admin_analytics_marketplace' },
      { id: 'analytics-search', path: '/analytics/search', label: 'جستجوهای کاربران', icon: 'Search', permission: 'admin_analytics_search' },
      { id: 'analytics-engagement', path: '/analytics/engagement', label: 'تعامل', icon: 'TouchApp', permission: 'admin_analytics_engagement' },
      { id: 'analytics-campaigns', path: '/analytics/campaigns', label: 'کمپین‌ها', icon: 'Campaign', permission: 'admin_analytics_campaigns' },
    ],
  },

  // ----- Misc -----
  {
    id: 'buyer-requests',
    path: '/buyer-requests',
    label: 'درخواست‌های خرید',
    icon: 'RequestQuote',
    permission: 'admin_buyer_requests_read',
  },
  {
    id: 'lottery',
    path: '/lottery',
    label: 'قرعه‌کشی‌ها',
    icon: 'EmojiEvents',
    permission: 'admin_lottery_read',
  },
  {
    id: 'raffle',
    path: '/raffle',
    label: 'قرعه‌کشی کاربران',
    icon: 'Casino',
    permission: 'admin_raffle_read',
  },
  { id: 'chats', path: '/chats', label: 'مدیریت چت‌ها', icon: 'ChatBubbleOutline', permission: 'admin_chats_disputes' },
  { id: 'chat-oversight', path: '/chat-oversight', label: 'نظارت بر چت‌ها', icon: 'Visibility', permission: 'admin_chats_monitor' },
  { id: 'userbot', path: '/userbot', label: 'دستیار گروه‌ها', icon: 'Groups', permission: 'admin_userbot_read' },
  { id: 'telegram-channels', path: '/telegram-channels', label: 'کانال‌های تلگرام فروشگاه‌ها', icon: 'Telegram', permission: 'admin_telegram_channels_read' },
  { id: 'reviews', path: '/reviews', label: 'مدیریت نظرات', icon: 'RateReview', permission: 'admin_reviews_read' },
  { id: 'review-reports', path: '/review-reports', label: 'گزارش‌های تخلف دیدگاه', icon: 'Flag', permission: 'admin_reviews_read' },
  { id: 'stories', path: '/stories', label: 'مدیریت استوری‌ها', icon: 'AutoStories', permission: 'admin_stories_read' },
  { id: 'inspections', path: '/inspections', label: 'بازرسی محصولات', icon: 'CheckCircle', permission: 'admin_inspections_list' },
  { id: 'notifications', path: '/notifications', label: 'اعلان‌ها', icon: 'Notifications', permission: 'admin_notifications' },

  // ----- Users / RBAC -----
  {
    id: 'users-group',
    label: 'کاربران و دسترسی‌ها',
    icon: 'People',
    children: [
      { id: 'users', path: '/users', label: 'مدیریت کاربران ادمین', icon: 'People', permission: 'admin_user_read' },
      { id: 'regular-users', path: '/regular-users', label: 'کاربران عادی', icon: 'Person', permission: 'admin_regular_user_read' },
      { id: 'roles', path: '/roles', label: 'مدیریت نقش‌ها', icon: 'VpnKey', permission: 'admin_role_read' },
      { id: 'permission-center', path: '/permission-center', label: 'مرکز دسترسی', icon: 'VpnKey', permission: ['admin_grant_list', 'admin_grant_read'] },
      { id: 'tenants', path: '/tenants', label: 'مدیریت سازمان‌ها', icon: 'Business', permission: 'admin_tenant_read' },
    ],
  },

  // ----- Work assignment & shift -----
  {
    id: 'work-group',
    label: 'شیفت‌بندی',
    icon: 'Schedule',
    children: [
      { id: 'work-profiles', path: '/admin-work-profiles', label: 'پروفایل‌های کاری', icon: 'Schedule', permission: 'admin_work_profile_read' },
      { id: 'holidays', path: '/holidays', label: 'تعطیلات', icon: 'CalendarToday', permission: 'admin_holiday_read' },
    ],
  },

  // ----- System / Settings -----
  {
    id: 'system-group',
    label: 'سیستم',
    icon: 'Settings',
    children: [
      { id: 'settings', path: '/settings', label: 'تنظیمات سیستم', icon: 'Settings', permission: 'admin_setting_read' },
      { id: 'seller-plans', path: '/seller-plans', label: 'پلن‌های فروشندگی', icon: 'WorkspacePremium', permission: 'admin_setting_read' },
      { id: 'ai-review', path: '/ai-review', label: 'وضعیت هوش مصنوعی', icon: 'SmartToy', permission: 'admin_setting_read' },
      { id: 'feature-flags', path: '/feature-flags', label: 'قابلیت‌های سایت (روشن/خاموش)', icon: 'PowerSettingsNew', permission: 'admin_feature_flags_read' },
      { id: 'store-policies', path: '/store-policies', label: 'نسخهٔ استورها (کافه‌بازار)', icon: 'Android', permission: 'admin_feature_flags_read' },
      { id: 'maintenance-windows', path: '/maintenance-windows', label: 'پنجره‌های به‌روزرسانی سایت', icon: 'Build', permission: 'admin_maintenance_read' },
      { id: 'app-updates', path: '/app-updates', label: 'به‌روزرسانی اپ', icon: 'Settings', permission: 'admin_app_release_read' },
      { id: 'figapoints-settings', path: '/figapoints-settings', label: 'تنظیمات گلاپوینت', icon: 'EmojiEvents', permission: 'admin_figapoints_settings_read' },
      { id: 'background-jobs', path: '/background-jobs', label: 'Background Jobs', icon: 'Schedule', permission: 'admin_background_jobs' },
      { id: 'audit-logs', path: '/audit-logs', label: 'لاگ تغییرات', icon: 'History', permission: ['admin_audit_read', 'admin_audit_logs'] },
      { id: 'user-activity', path: '/user-activity', label: 'فعالیت کاربران', icon: 'History', permission: 'admin_user_activity_read' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers — used by Sidebar and RequireAccess
// ---------------------------------------------------------------------------

/**
 * Returns a flat list of every permission required somewhere in the tree.
 * Useful for the Permission Center "Catalog" tab and audit scripts.
 */
export function collectAllMenuPermissions(): PermissionCode[] {
  const set = new Set<PermissionCode>();
  const walk = (e: MenuEntry) => {
    if ('permission' in e && e.permission) {
      const arr = Array.isArray(e.permission) ? e.permission : [e.permission];
      for (const p of arr) set.add(p);
    }
    if (isMenuGroup(e)) e.children.forEach(walk);
  };
  ADMIN_MENUS.forEach(walk);
  return Array.from(set);
}

/**
 * First leaf (in sidebar order) whose permission the caller actually holds.
 *
 * Used as the post-login landing target. Sending everyone to a hard-coded
 * `/dashboard` bounced any admin whose role lacks `admin_dashboard` straight
 * to /403 with no way back into the panel.
 *
 * Returns null when the user can open no menu entry at all.
 */
export function findFirstAccessibleLeaf(
  has: (perm: MenuPermissionRule) => boolean
): MenuLeaf | null {
  const walk = (entries: MenuEntry[]): MenuLeaf | null => {
    for (const e of entries) {
      if (isMenuGroup(e)) {
        const hit = walk(e.children);
        if (hit) return hit;
      } else if (isLeafVisible(e, has)) {
        return e;
      }
    }
    return null;
  };
  return walk(ADMIN_MENUS);
}

/**
 * Visibility of a single leaf: `permission: null` means every authenticated
 * admin. Shared by the Sidebar filter and the post-login landing resolver so
 * the two can never disagree.
 */
export function isLeafVisible(
  leaf: MenuLeaf,
  has: (perm: MenuPermissionRule) => boolean
): boolean {
  return leaf.permission === null || has(leaf.permission);
}

/**
 * Find the first leaf matching a path (used for breadcrumb / route guard).
 */
export function findLeafByPath(path: string): MenuLeaf | null {
  const walk = (entries: MenuEntry[]): MenuLeaf | null => {
    for (const e of entries) {
      if (!isMenuGroup(e) && e.path === path) return e;
      if (isMenuGroup(e)) {
        const hit = walk(e.children);
        if (hit) return hit;
      }
    }
    return null;
  };
  return walk(ADMIN_MENUS);
}
